/**
 * Remembered crew slots — the pure display rules.
 *
 * One foreman's reports print crew slots (`LABORER-ID11`, `OPER-IDPF`) in the
 * labor rows instead of people. The PM decides once, the portal repeats it,
 * and the whole feature rests on the repetition being VISIBLE: a stored
 * decision that applied itself quietly would be a billing identity nobody
 * chose today.
 *
 * So both rules here are about honesty on screen. The checkbox appears only
 * where the server would accept it, and every line the decision reaches says
 * what the page actually printed.
 */
import type { InboxBlockForeman, InboxPlaceholderRemembered } from '@coffman-tm/shared';

/**
 * May the reassign dialog offer "Remember this"?
 *
 * Mirrors the server's three refusals, so a box the PM can tick is a box that
 * will be accepted — a tick that comes back as a 400 teaches him to distrust
 * the dialog. Needs a foreman key on the report, a printed id on the line,
 * and a line that is either UNKNOWN (the first decision) or already carrying a
 * remembered fill (a replacement, which has to be one click — crew slots
 * genuinely change hands).
 */
export function canOfferRemember(args: {
  foreman: InboxBlockForeman | null | undefined;
  rawId: string | null | undefined;
  isUnknown: boolean;
  printedId: string | undefined;
}): boolean {
  if (!args.foreman?.hj_id) return false;
  if (typeof args.rawId !== 'string' || args.rawId.trim().length === 0) return false;
  return args.isUnknown || args.printedId !== undefined;
}

/**
 * The checkbox label. The foreman's name when the page carried one — the
 * decision is scoped to HIS reports and the label has to say so — and a
 * generic phrasing when it did not, because the key is the ID and the label
 * must not claim a name nobody read.
 */
export function rememberCheckboxLabel(foremanName: string | null | undefined): string {
  const name = foremanName?.trim();
  return name ? `Remember this for ${name}’s reports` : 'Remember this for this foreman’s reports';
}

/**
 * The confirmation. It reports the two things the PM cannot see from the card
 * he is looking at: that later reports will do this on their own, and how many
 * lines already waiting were just filled.
 */
export function rememberToast(r: InboxPlaceholderRemembered): {
  variant: 'success';
  title: string;
  body: string;
} {
  const filled =
    r.filled_other_lines === 0
      ? `No other waiting lines used this ID.`
      : `Filled ${r.filled_other_lines} other waiting ${
          r.filled_other_lines === 1 ? 'line' : 'lines'
        } with the same ID.`;
  const foreman = r.foreman_name.trim() || 'this foreman';

  if (r.replaced_previous) {
    return {
      variant: 'success',
      title: `${r.placeholder_id} now means ${r.employee_name}`,
      body:
        `Later reports from ${foreman} will use ${r.employee_name} for this ID. ` +
        `Lines already filled with the previous person keep it — change those on the line. ` +
        filled,
    };
  }
  return {
    variant: 'success',
    title: `Remembered ${r.placeholder_id} as ${r.employee_name}`,
    body: `Later reports from ${foreman} will fill this line in automatically. ${filled}`,
  };
}

/**
 * The reassign landed, the decision did not. Says BOTH halves: "reassigned"
 * alone would leave the PM believing the portal will repeat it on tomorrow's
 * report, and he would find out by re-typing the line six times. Ops has
 * already been alerted server-side, which is what makes naming Brandon here
 * true rather than reassuring.
 */
export function rememberFailedToast(): {
  variant: 'warning';
  title: string;
  body: string;
} {
  return {
    variant: 'warning',
    title: 'Reassigned, but not remembered',
    body: "The line is pointed at the right person, but the portal couldn't save the decision — later reports will still arrive unassigned. Support has been notified; try ticking the box again on the next one.",
  };
}

/**
 * The "PDF says …" marker's text for one labor line, or null for no marker.
 *
 * Suppressed when the line's existing "PDF: …" name note already prints that
 * exact string — the ordinary crew-slot shape, where the page put the slot in
 * both the ID and the name column. Two identical markers on one line is noise,
 * and noise is how a disclosure stops being read.
 */
export function printedIdMarkerFor(row: {
  printed_id?: string;
  employee_name_raw: string;
  name: string;
}): string | null {
  const printed = row.printed_id;
  if (printed === undefined) return null;
  const nameMarkerShows = row.employee_name_raw !== row.name;
  if (nameMarkerShows && row.employee_name_raw === printed) return null;
  return printed;
}
